import { useSelector } from "react-redux";
import { TYPE_QUESTIONS, useGameSounds } from "@educaplay/core";
import Point from "../../Point";
import { isUnansweredAnswer } from "@educaplay/store/utils/functions";
import { useState, useEffect } from "react"
import { useDispatch } from "react-redux";
import { goToQuestion, setShowBankControl } from "@educaplay/store/slices/gameSlice";
import useMap from "../../../hooks/useMap";
import { MAPA_SOUNDS, STATE_POINT, TYPE_POINTS } from "../../../utils/constants";
import { DialogContent } from "../../DialogContent";
import SwipeableDrawerPoint from "../../SwipeableDrawerPoint";
import useNavigationQuestion from "../../../hooks/useNavigationQuestion";

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const customEventHiddenButton = new Event("hiddenButton", { bubbles: true });


export const InteractivePointSelection = (props) => {

  const { id, onAnswer, disabled, passScreenHandler, setShowMessage, setIsInfoScreenActive, isInfoScreenActive } = props;

  const allQuestions = useSelector(state => state.game.questions);
  const currentQuestion = useSelector(state => allQuestions[state.game.currentIndex]);
  const screenId = useSelector(state => state.game.questions[state.game.currentIndex].screenId);  
  const questions = allQuestions.filter(question => question.screenId === screenId);
  const answeredQuestions = questions.filter(question => !isUnansweredAnswer(question.answer,{examMode: false}) && question.checkAnswer(question.answer)).length;
  const isAllQuestionsScreenAnswered = questions.every(question => !isUnansweredAnswer(question.answer,{examMode: false}) && question.checkAnswer(question.answer));
  const isLastPointVisited = answeredQuestions === questions.length - 1;
  const [hasError, setHasError] = useState(false);
  const [hasAcert, setHasAcert] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const pointDisabled = (disabled || hasError);
  const questionSelected = questions.find(question => question.id === id);
  const dispatch = useDispatch();
  const { zoomToPoint, reset } = useMap();
  const [state, setState] = useState(STATE_POINT.DEFAULT);
  const [showBalloon, setShowBalloon] = useState(true);
  const gameSound = useGameSounds();
  const [passQuestion, setPassQuestion] = useState(false);
  const { onChangeEnablePassQuestion, enablePassQuestion } = useNavigationQuestion();
  const [visitPoint, setVisitPoint] = useState(false);
  const liveActived = useSelector(state => state.game.liveActived);

  useEffect(() => {
    setHasError(false);
    if (state === STATE_POINT.INCORRECT) setState(STATE_POINT.DEFAULT);
    setShowBalloon(true);


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion.id])



  useEffect(() => {
    dispatch(setShowBankControl(true));
    setIsInfoScreenActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const resetValues = () => {
      if (state !== STATE_POINT.FOCUS || (!enablePassQuestion && !visitPoint)) return;
      reset();

      gameSound.play(MAPA_SOUNDS.CHARGE);
      setShowInfoDialog(false);
      setState(STATE_POINT.CORRECT);
      setShowMessage(true);


      if (isLastPointVisited) {
        document.dispatchEvent(customEventHiddenButton);
      }

      onChangeEnablePassQuestion(true);
      dispatch(setShowBankControl(true));
      setVisitPoint(false);
    }

    document.addEventListener("clickPassButton", resetValues)

    return () => {
      document.removeEventListener("clickPassButton", resetValues)
    }


  }, [dispatch, enablePassQuestion, isLastPointVisited, onChangeEnablePassQuestion, reset, setShowMessage, state, gameSound, setShowInfoDialog, visitPoint])


  const findNextQuestionIndex = () => {
    if (questions.length === 1) return;
    const currentIndex = questions.findIndex(question => question.id === currentQuestion.id);
    const questionsOrdered = [...questions.slice(currentIndex + 1), ...questions.slice(0, currentIndex)]
    const nextQuestion = questionsOrdered.find((question) => isUnansweredAnswer(question.answer) || (!isUnansweredAnswer(question.answer) && !question.checkAnswer(question.answer)));

    const nextQuestionIndex = nextQuestion ?
      allQuestions.findIndex(({ id }) => id === nextQuestion.id) :
      allQuestions.findIndex((question) => question.id === questions.find(({ id }) => id !== currentQuestion.id).id);
    return nextQuestionIndex;
  }

  const onCloseAdittionalInfo = () => {
    if (!showInfoDialog) return;
    dispatch(setShowBankControl(true));
    setShowMessage(true);
    gameSound.play(MAPA_SOUNDS.CHARGE);
    setState(STATE_POINT.CORRECT);
    setShowInfoDialog(false);
    onChangeEnablePassQuestion(true);
    reset();
    if (isLastPointVisited) {
      document.dispatchEvent(customEventHiddenButton);
    }
    if (isAllQuestionsScreenAnswered) {
      passScreenHandler();
      return;
    }
    if (passQuestion) {
      dispatch(goToQuestion(findNextQuestionIndex()));
      setPassQuestion(false);
    }
  }

  const onCloseAutomatic = () => {
    if (answeredQuestions === questions.length - 2) document.dispatchEvent(customEventHiddenButton);
    setIsInfoScreenActive(false);
    dispatch(setShowBankControl(true));
    setShowMessage(true);
    gameSound.play(MAPA_SOUNDS.CHARGE);
    setState(STATE_POINT.CORRECT);
    setShowInfoDialog(false);
    onChangeEnablePassQuestion(true);
    reset();
    if (!isLastPointVisited) dispatch(goToQuestion(findNextQuestionIndex()));
  }

  const showInfoScreen = async (duration = null) => {
    onChangeEnablePassQuestion(false);
    if (!duration) return;

    await wait(duration);

    onChangeEnablePassQuestion(true);
  }

  const onClickHandler = async () => {
    if (isInfoScreenActive && !hasAcert) return;

    if (hasAcert) {
      setPassQuestion(false);
      onChangeEnablePassQuestion(false);
      dispatch(setShowBankControl(false));
      setShowMessage(false);
      zoomToPoint(questionSelected)
      setState(STATE_POINT.FOCUS);
      showInfoScreen();
      setVisitPoint(true);
      await wait(200);
      setShowInfoDialog(true);


      return;
    }
    gameSound.play(MAPA_SOUNDS.GOODACTIVE);

    const userAnswer = {
      type: TYPE_QUESTIONS.Selection,
      answer: id,
    }

    const questionIndex = allQuestions.findIndex(question => question.id === currentQuestion.id);
    onAnswer(userAnswer, {
      questionIndexAnswer: questionIndex,
      nextQuestionIndex: questionIndex,
      hiddenAdditionalInfo: true,
      showFeedback: !(currentQuestion.checkAnswer(userAnswer) && !liveActived)
    });

    const isCorrect = currentQuestion.checkAnswer(userAnswer);

    if (isCorrect) {
      showInfoScreen(800);
      if (currentQuestion.explanation) setPassQuestion(true);
      gameSound.play(MAPA_SOUNDS.CORRECT);
      setShowBalloon(true);
      setHasAcert(true);
      setState(STATE_POINT.FOCUS);
      await zoomToPoint(currentQuestion);
      setShowMessage(false);

      if (!currentQuestion.explanation && !isLastPointVisited) {
        setIsInfoScreenActive(true);
        setTimeout(() => {
          onCloseAutomatic();
        }, 600);
        return;
      }



      if (!isLastPointVisited) setShowInfoDialog(true);
    } else {
      setHasError(true);
      setState(STATE_POINT.INCORRECT);

      await reset();

      const balloonTimeOut = setTimeout(() => {
        setShowBalloon(false);
        clearTimeout(balloonTimeOut);
      }, 1600)

    }
  }

  useEffect(() => {
    setIsInfoScreenActive(showInfoDialog);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInfoDialog])

  useEffect(() => {
    if (isAllQuestionsScreenAnswered) {
      setTimeout(() => {
        passScreenHandler();
      }, 200);
    }
  }, [isAllQuestionsScreenAnswered, passScreenHandler])


  return (
    <>
      <SwipeableDrawerPoint open={showInfoDialog} onClose={onCloseAdittionalInfo}>
        <DialogContent
          mediaType={questionSelected.media?.type}
          media={questionSelected.media?.src}
          title={questionSelected.title}
          description={questionSelected.description}
          explanation={questionSelected.explanation} />
      </SwipeableDrawerPoint>

      <Point
        onClick={onClickHandler}
        disabled={pointDisabled}
        type={TYPE_POINTS.SELECTION}
        state={state}
        showBalloon={showBalloon}
        isInfoScreenActive={isInfoScreenActive}
      />
    </>
  )


}